import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { BookOpen, Users, TrendingUp, Shield, ArrowRight, Search } from 'lucide-react';
import Navbar from '../components/Navbar';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-4 gap-8">
          <div>
            <h3 className="text-xl font-serif font-bold mb-4">The Case Verse</h3>
            <p className="text-slate-400 text-sm">Premium case studies for business education</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/cases" className="text-slate-400 hover:text-white">Case Library</Link></li>
              <li><Link to="/videos" className="text-slate-400 hover:text-white">Videos</Link></li>
              <li><Link to="/research" className="text-slate-400 hover:text-white">Research</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-slate-400 hover:text-white">About Us</Link></li>
              <li><Link to="/team" className="text-slate-400 hover:text-white">Team</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/privacy" className="text-slate-400 hover:text-white">Terms of Use</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm text-slate-400">
          © 2026 The Case Verse. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [heroQuery, setHeroQuery] = useState('');

  const handleHeroSearch = (e) => {
    e.preventDefault();
    const q = heroQuery.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <section className="pt-32 pb-24 px-6 noise-bg" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-8">
            <motion.div
              className="col-span-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-6xl font-serif font-bold tracking-tight text-slate-900 mb-6">
                Where Business Learning Meets Real-World Decisions
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                The Case Verse is one of the world's most advanced and dynamic repositories of teaching case studies and interactive simulations — purpose-built to take academic and executive learning to the next level. Trusted by business schools and corporate learning academies alike, we combine premium cases, instructor manuals, student worksheets and immersive decision-making simulations in a single platform.
              </p>

              {/* Public keyword search + login pill side-by-side */}
              <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mb-6" data-testid="hero-search-row">
                <form onSubmit={handleHeroSearch} className="relative flex-1" data-testid="hero-search-form">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={heroQuery}
                    onChange={(e) => setHeroQuery(e.target.value)}
                    placeholder="Enter Keywords, Titles, Concepts"
                    className="w-full pl-12 pr-28 py-4 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 bg-white shadow-sm"
                    data-testid="hero-search-input"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-medium text-sm"
                    data-testid="hero-search-button"
                  >
                    Search
                  </button>
                </form>
                {!user && (
                  <>
                    <Link
                      to="/subscription"
                      className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-medium text-base whitespace-nowrap shadow-sm"
                      data-testid="hero-subscribe-now-button"
                    >
                      Subscribe Now
                    </Link>
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all font-medium text-base whitespace-nowrap shadow-sm"
                      data-testid="hero-login-signup-button"
                    >
                      Login / Sign Up
                    </Link>
                  </>
                )}
              </div>

              <div className="flex gap-4">
                {user ? (
                  <Link
                    to="/cases"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all text-lg font-medium"
                    data-testid="hero-browse-cases-button"
                  >
                    Browse Case Library <ArrowRight className="w-5 h-5" />
                  </Link>
                ) : null}
              </div>
            </motion.div>
            <motion.div
              className="col-span-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1769092992447-18050cf9bd26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwc3R1ZGVudHMlMjBzdHVkeWluZyUyMGxpYnJhcnl8ZW58MHx8fHwxNzcxMDAzMjkyfDA&ixlib=rb-4.1.0&q=85&w=600"
                alt="Students studying"
                className="rounded shadow-lg w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-16 text-center">
            Why The Case Verse?
          </h2>
          <div className="grid grid-cols-4 gap-8">
            <motion.div
              className="p-8 border border-slate-200 rounded hover:border-slate-900 transition-all"
              whileHover={{ y: -4 }}
              data-testid="feature-card-comprehensive"
            >
              <BookOpen className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-serif font-semibold mb-3">Comprehensive Library</h3>
              <p className="text-slate-600">Access case studies across Finance, Marketing, Technology, and more</p>
            </motion.div>
            <motion.div
              className="p-8 border border-slate-200 rounded hover:border-slate-900 transition-all"
              whileHover={{ y: -4 }}
              data-testid="feature-card-instructor"
            >
              <Users className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-serif font-semibold mb-3">Instructor Resources</h3>
              <p className="text-slate-600">Complete teaching notes and student worksheets included</p>
            </motion.div>
            <motion.div
              className="p-8 border border-slate-200 rounded hover:border-slate-900 transition-all"
              whileHover={{ y: -4 }}
              data-testid="feature-card-flexible"
            >
              <TrendingUp className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-serif font-semibold mb-3">Flexible Subscriptions</h3>
              <p className="text-slate-600">Choose by tenure and product type to fit your needs</p>
            </motion.div>
            <motion.div
              className="p-8 border border-slate-200 rounded hover:border-slate-900 transition-all"
              whileHover={{ y: -4 }}
              data-testid="feature-card-secure"
            >
              <Shield className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-serif font-semibold mb-3">Secure Access</h3>
              <p className="text-slate-600">Screen-only reading with content protection</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-slate-50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-6">
            Subscribe to The Case Verse
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Unlock our full catalogue of premium case studies, teaching notes, student worksheets and interactive simulations with an academic or corporate learning subscription.
          </p>
          {!user && (
            <Link
              to="/subscription"
              className="inline-flex items-center gap-2 px-8 py-4 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all text-lg font-medium"
              data-testid="cta-subscribe-button"
            >
              View Subscription Plans <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
